import { useEffect, useState } from 'react';
import {
  Box,
  Button,
  Chip,
  CircularProgress,
  Collapse,
  IconButton,
  Link,
  Typography,
} from '@mui/material';
import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown';
import KeyboardArrowUpIcon from '@mui/icons-material/KeyboardArrowUp';
import { OrdersService } from '../../../api/services/ordersService';
import {
  IGetOrderCallbacksResponse,
  IOrderCallbackEvent,
} from '../../../types/api/response/ordercallbacksresponse';
import { statusColor } from './statusColor';

interface Props {
  orderNumber: string;
  // First page, fetched by the dialog (which also uses it to decide whether the tab shows at all).
  // null = authorized but the request failed.
  firstPage: IGetOrderCallbacksResponse | null;
}

const PAGE_SIZE = 20;

const formatDate = (val?: string | null) => {
  if (!val) return '—';
  return new Date(val).toLocaleString();
};

const outcomeColor = (outcome: string | null): 'default' | 'success' | 'warning' | 'error' => {
  switch (outcome) {
    case 'APPLIED':
      return 'success';
    // Persisted but never finished applying — only left behind if the process died mid-way.
    case 'RECEIVED':
      return 'warning';
    case 'REJECTED':
    case 'FAILED':
      return 'error';
    // DUPLICATE, SUPERSEDED — acknowledged, deliberately not applied.
    default:
      return 'default';
  }
};

const DetailRow = ({ label, value }: { label: string; value?: React.ReactNode }) => {
  if (value == null || value === '') return null;
  return (
    <Box mt={1}>
      <Typography variant="caption" color="text.secondary" fontWeight={600}>
        {label}
      </Typography>
      <Typography variant="body2" component="div" mt={0.25} sx={{ wordBreak: 'break-word' }}>
        {value}
      </Typography>
    </Box>
  );
};

const PayloadBlock = ({ payload }: { payload: Record<string, unknown> | null }) => {
  if (!payload) return null;
  return (
    <Box mt={1}>
      <Typography variant="caption" color="text.secondary" fontWeight={600}>
        Payload
      </Typography>
      <Typography
        variant="body2"
        component="pre"
        sx={{
          whiteSpace: 'pre-wrap',
          wordBreak: 'break-word',
          fontFamily: 'monospace',
          fontSize: 11.5,
          backgroundColor: '#fafafa',
          border: '1px solid',
          borderColor: 'divider',
          borderRadius: 1,
          p: 1,
          mt: 0.5,
          maxHeight: 260,
          overflow: 'auto',
        }}
      >
        {JSON.stringify(payload, null, 2)}
      </Typography>
    </Box>
  );
};

export const OrderCallbacksPanel = ({ orderNumber, firstPage }: Props) => {
  const [events, setEvents] = useState<IOrderCallbackEvent[]>(firstPage?.content ?? []);
  const [pageNumber, setPageNumber] = useState(0);
  const [loadingMore, setLoadingMore] = useState(false);
  const [loadMoreFailed, setLoadMoreFailed] = useState(false);
  const [expandedId, setExpandedId] = useState<number | null>(null);

  // A new first page (dialog reopened, or refreshed after a rerun) restarts the list.
  useEffect(() => {
    setEvents(firstPage?.content ?? []);
    setPageNumber(0);
    setLoadMoreFailed(false);
    setExpandedId(null);
  }, [firstPage]);

  if (firstPage === null) {
    return (
      <Typography variant="body2" color="error">
        Failed to load callbacks for this order.
      </Typography>
    );
  }

  if (events.length === 0) {
    return (
      <Typography variant="body2" color="text.secondary">
        No delivery callbacks received for this order.
      </Typography>
    );
  }

  const totalElements = firstPage.page.totalElements;
  const hasMore = events.length < totalElements;

  const loadMore = () => {
    const next = pageNumber + 1;
    setLoadingMore(true);
    setLoadMoreFailed(false);
    OrdersService.getOrderCallbacks(orderNumber, next, PAGE_SIZE)
      .then((res) => {
        setEvents((prev) => [...prev, ...res.content]);
        setPageNumber(next);
      })
      .catch(() => setLoadMoreFailed(true))
      .finally(() => setLoadingMore(false));
  };

  return (
    <Box display="flex" flexDirection="column" gap={1}>
      <Typography variant="caption" color="text.secondary">
        Newest received first · {events.length} of {totalElements}
      </Typography>

      {events.map((event) => {
        const isExpanded = expandedId === event.id;
        return (
          <Box
            key={event.id}
            sx={{
              border: '1px solid',
              borderColor: 'divider',
              borderRadius: 1,
              overflow: 'hidden',
            }}
          >
            <Box
              display="flex"
              alignItems="flex-start"
              gap={1}
              p={1}
              sx={{ cursor: 'pointer' }}
              onClick={() => setExpandedId(isExpanded ? null : event.id)}
            >
              <IconButton size="small">
                {isExpanded ? (
                  <KeyboardArrowUpIcon fontSize="small" />
                ) : (
                  <KeyboardArrowDownIcon fontSize="small" />
                )}
              </IconButton>
              <Box sx={{ flex: 1, minWidth: 0 }}>
                <Box display="flex" alignItems="center" gap={1}>
                  <Typography variant="caption" color="text.secondary" sx={{ minWidth: 140 }}>
                    {formatDate(event.received_at)}
                  </Typography>
                  <Typography
                    variant="caption"
                    sx={{ fontFamily: 'monospace', flex: 1, minWidth: 0 }}
                    noWrap
                  >
                    {event.dispatch_id}
                  </Typography>
                </Box>
                <Box display="flex" gap={0.75} flexWrap="wrap" mt={0.5}>
                  <Chip
                    label={event.status ?? '—'}
                    size="small"
                    color={statusColor(event.status)}
                  />
                  {event.sub_status && (
                    <Chip label={event.sub_status} size="small" variant="outlined" />
                  )}
                  <Chip
                    label={event.source}
                    size="small"
                    variant="outlined"
                    color={event.source === 'SUPERPRO' ? 'secondary' : 'default'}
                  />
                  {event.outcome && (
                    <Chip label={event.outcome} size="small" color={outcomeColor(event.outcome)} />
                  )}
                </Box>
              </Box>
            </Box>
            <Collapse in={isExpanded} unmountOnExit>
              <Box px={1.5} pb={1.5}>
                <DetailRow label="Occurred at (as sent)" value={event.occurred_at} />
                <DetailRow
                  label="Tour"
                  value={
                    event.tour_id
                      ? `${event.tour_id}${event.tour_sequence != null ? ` · stop ${event.tour_sequence}` : ''}`
                      : null
                  }
                />
                {/* On a SuperPro callback, tour_id is SuperPro's own tour id; show the Locus one too. */}
                {event.locus_tour_id && event.locus_tour_id !== event.tour_id && (
                  <DetailRow label="Locus tour" value={event.locus_tour_id} />
                )}
                <DetailRow
                  label="Tracking link"
                  value={
                    event.tracking_link ? (
                      <Link href={event.tracking_link} target="_blank" rel="noopener noreferrer">
                        {event.tracking_link}
                      </Link>
                    ) : null
                  }
                />
                <PayloadBlock payload={event.payload} />
              </Box>
            </Collapse>
          </Box>
        );
      })}

      {loadMoreFailed && (
        <Typography variant="body2" color="error">
          Failed to load more callbacks.
        </Typography>
      )}
      {hasMore && (
        <Box display="flex" justifyContent="center">
          <Button size="small" onClick={loadMore} disabled={loadingMore}>
            {loadingMore ? <CircularProgress size={16} /> : 'Load more'}
          </Button>
        </Box>
      )}
    </Box>
  );
};
